import { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table";
import {
  FileText,
  Upload,
  Search,
  Filter,
  MoreVertical,
  Download,
  Trash2,
  Sparkles,
  Loader2,
  X,
  CheckCircle2,
  Eye
} from "lucide-react";
import { toast } from "sonner";
import PDFPreview from "../components/PDFPreview";

const CATEGORIES = ["Contrat", "Facture", "CV", "Note de service", "Courrier", "Autre"];

const getCategoryClass = (category) => {
  const classes = {
    "Contrat": "category-contrat",
    "Facture": "category-facture",
    "CV": "category-cv",
    "Note de service": "category-note",
    "Courrier": "category-courrier",
    "Autre": "category-autre"
  };
  return classes[category] || "category-autre";
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " Ko";
  return (bytes / (1024 * 1024)).toFixed(1) + " Mo";
};

export default function DocumentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get("category") || "all");
  const [uploadResult, setUploadResult] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    // Read category from URL on mount
    const urlCategory = searchParams.get("category");
    if (urlCategory && urlCategory !== categoryFilter) {
      setCategoryFilter(urlCategory);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchDocuments();
    // Update URL when category changes
    if (categoryFilter && categoryFilter !== "all") {
      setSearchParams({ category: categoryFilter });
    } else {
      setSearchParams({});
    }
  }, [categoryFilter]);

  const fetchDocuments = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== "all") {
        params.append("category", categoryFilter);
      }
      if (search) {
        params.append("search", search);
      }
      const response = await api.get(`/documents?${params.toString()}`);
      setDocuments(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDocuments();
  };

  const clearCategoryFilter = () => {
    setCategoryFilter("all");
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setUploadResult(response.data);
      fetchDocuments();
      toast.success("Document importé avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1,
    disabled: uploading
  });

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    try {
      await api.delete(`/documents/${deleteDoc.id}`);
      toast.success("Document supprimé");
      setDeleteDoc(null);
      fetchDocuments();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Documents</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Gérez vos documents avec classification électronique automatique
        </p>
      </div>

      {/* Active Category Filter Banner */}
      {categoryFilter && categoryFilter !== "all" && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex items-center gap-2 sm:gap-3">
            <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
            <span className="text-sm sm:text-base font-medium">
              Filtre : <Badge className={getCategoryClass(categoryFilter)}>{categoryFilter}</Badge>
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearCategoryFilter}
            className="text-primary hover:text-primary self-end sm:self-auto"
            data-testid="clear-filter-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Effacer
          </Button>
        </div>
      )}

      {/* Upload Zone */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer transition-all
              ${isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"}
              ${uploading ? "opacity-50 cursor-not-allowed" : ""}
            `}
            data-testid="upload-dropzone"
          >
            <input {...getInputProps()} data-testid="file-input" />
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4 ai-processing">
                  <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-primary animate-spin" />
                </div>
                <p className="text-base sm:text-lg font-medium">Analyse en cours...</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Classification et extraction du texte
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-muted flex items-center justify-center mb-3 sm:mb-4">
                  <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
                </div>
                <p className="text-base sm:text-lg font-medium">
                  {isDragActive
                    ? "Déposez le fichier ici"
                    : "Glissez-déposez un PDF"}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  ou cliquez pour sélectionner
                </p>
                <div className="flex items-center gap-2 mt-3 sm:mt-4 text-xs text-muted-foreground">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-violet-500" />
                  Classification et OCR automatiques
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload Result Dialog */}
      <Dialog open={!!uploadResult} onOpenChange={() => setUploadResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Document importé
            </DialogTitle>
          </DialogHeader>
          {uploadResult && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-10 h-10 text-primary" />
                  <div>
                    <p className="font-medium">{uploadResult.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatFileSize(uploadResult.file_size)}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Catégorie détectée:</span>
                    <Badge className={getCategoryClass(uploadResult.category)}>
                      {uploadResult.ai_classified && (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      {uploadResult.category}
                    </Badge>
                  </div>
                  {uploadResult.suggested_name && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Nom suggéré:</span>
                      <span className="font-mono text-xs">
                        {uploadResult.suggested_name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setUploadResult(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 text-sm"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary" size="sm" className="px-3 sm:px-4" data-testid="search-btn">
                <Search className="w-4 h-4 sm:hidden" />
                <span className="hidden sm:inline">Rechercher</span>
              </Button>
            </form>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {documents.length} document{documents.length !== 1 ? "s" : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-50" />
              <p className="text-base sm:text-lg">Aucun document trouvé</p>
              <p className="text-sm mt-1">
                Importez votre premier document PDF
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {doc.name}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge className={`${getCategoryClass(doc.category)} text-xs`}>
                              {doc.category}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatFileSize(doc.file_size)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(doc.created_at)}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 flex-shrink-0"
                            data-testid={`doc-menu-mobile-${doc.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setPreviewDoc(doc)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Aperçu
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownload(doc)}>
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteDoc(doc)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Taille</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Importé par</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id} className="group">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate max-w-[200px]">
                                {doc.name}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">
                                {doc.file_hash.slice(0, 16)}...
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getCategoryClass(doc.category)}>
                            {doc.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatFileSize(doc.file_size)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(doc.created_at)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {doc.uploaded_by_name}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`doc-menu-${doc.id}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setPreviewDoc(doc)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Aperçu
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDeleteDoc(doc)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDoc} onOpenChange={() => setDeleteDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le document ?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Cette action est irréversible. Le document "{deleteDoc?.name}" sera
            définitivement supprimé.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDoc(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview */}
      {previewDoc && (
        <PDFPreview
          documentId={previewDoc.id}
          documentName={previewDoc.name}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}
